import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import type { CreateOptions, RequestOptions, Result } from '@/utils/axios/axiosModel'
import { AxiosCanceler } from './axiosCancel'
import { ContentTypeEnum } from '@/enums/axios'
import { getToken } from '@/utils/auth'
import { isFunc, isFormData } from '@/utils/is'
import { turnDataToFormData } from './utils'
import { cloneDeep, sleep } from '@/utils/tools'
import qs from 'qs'
// @ts-ignore
import { LRUCache } from 'lru-cache'
import { isProdMode } from '../env'

// 定义缓存键生成函数，根据请求配置生成唯一键
function generateCacheKey(config: AxiosRequestConfig): string {
  const { method, url, params, data } = config // 请求方式，参数，请求地址，
  let cacheKey = ''
  try {
    cacheKey = typeof data === 'object' ? data : JSON.parse(data || '{}')
  } catch (error) {
    /* empty */
  }
  return [method, url, qs.stringify(params), qs.stringify(cacheKey)].join('&').replace(/&+$/, '') // 拼接
}
// 定义缓存类
class AxiosCache {
  private cache: LRUCache<string, AxiosResponse<any>>

  constructor(maxSize: number, maxAgeMs: number) {
    this.cache = new LRUCache<string, AxiosResponse<any>>({
      max: maxSize,
      ttl: maxAgeMs
    })
  }

  public async get<T>(config: AxiosRequestConfig): Promise<AxiosResponse<T> | undefined> {
    const key = generateCacheKey(config)
    const cachedResponse = this.cache.get(key)
    return cachedResponse as AxiosResponse<T> | undefined
  }

  public async set<T>(config: AxiosRequestConfig, response: AxiosResponse<T>): Promise<void> {
    const key = generateCacheKey(config)
    // @ts-ignore
    this.cache.set(key, response)
  }
}
const axiosCache = new AxiosCache(20, 200 * 1) // 最大缓存20个条目 缓存900ms
const abortControllerMap: Map<string, AbortController> = new Map()
const generateSignal = (config: AxiosRequestConfig) => {
  // @ts-ignore
  if (!config?.requestOptions?.canCancel) return
  const controller = new AbortController()
  config.signal = controller.signal
  abortControllerMap.set(generateCacheKey(config), controller)
}

/**
 * @author：张胜
 * @desc：网络请求封装
 * */
export class RxkAxios {
  private readonly options: CreateOptions
  private instance: AxiosInstance // 实例
  constructor(options: any) {
    this.options = options
    this.instance = axios.create(options)
    this.setupInterceptors()
  }
  // 拦截器
  setupInterceptors() {
    const transform = this.getTransform()
    if (!transform) {
      return
    }
    const { requestInterceptors, responseInterceptors, responseInterceptorsCatch } = transform
    const axiosCanceler = new AxiosCanceler()
    // axios请求拦截处理
    this.instance.interceptors.request.use(async config => {
      // 判断如果是以 dis/ 开头的接口，修改为 www.b.com
      if (isProdMode() && config.url?.startsWith('/dis/')) {
        config.baseURL = import.meta.env.VITE_DIS_BASE_URL_DIS
      }
      // @ts-ignore
      const { ignoreCancelToken } = config.requestOptions
      // 是否添加ignoreCancelToken字段判断是否需要忽略cancelToken
      const ignoreCancel =
        ignoreCancelToken !== undefined
          ? ignoreCancelToken
          : this.options.requestOptions?.ignoreCancelToken
      // 如果需要使用cancelToken
      !ignoreCancel && axiosCanceler.addPending(config)
      if (requestInterceptors && isFunc(requestInterceptors)) {
        config = requestInterceptors(config, this.options)
      }
      generateSignal(config)
      const cachedResponse = await axiosCache.get(config)
      if (cachedResponse) {
        await sleep(50) // 缓存
        return Promise.reject({
          message: 'Request was canceled due to cached response.',
          cache: 1,
          response: cloneDeep(cachedResponse)
        })
      }
      return config
    }, undefined)
    // axios响应拦截处理
    this.instance.interceptors.response.use(
      async res => {
        await axiosCache.set(res.config, res)
        // 请求完成后移除对应的cancelToken
        res && axiosCanceler.removePending(res.config)
        if (responseInterceptors && isFunc(responseInterceptors)) {
          res = responseInterceptors(res)
        }
        return res
      },
      e => {
        const { cache, response } = e
        // 命中内存缓存时按成功返回，避免走进错误处理
        if (cache) {
          if (responseInterceptors && isFunc(responseInterceptors)) {
            return Promise.resolve(responseInterceptors(response))
          }
          return Promise.resolve(response)
        }
        if (responseInterceptorsCatch && isFunc(responseInterceptorsCatch)) {
          return responseInterceptorsCatch(e)
        }
        return Promise.reject(e)
      }
    )
  }
  /**
   * @description: 上传接口,一般上传接口不同于其他接口
   * @param {AxiosRequestConfig} config
   * @param {UploadParams} uploadParams
   * @return {*}
   */
  upload(config: any, requestOptions = {}) {
    const { data, headers = {} } = config
    const formData = isFormData(data) ? data : turnDataToFormData(data)
    return this.request(
      {
        method: 'POST', // 默认post接求方式
        ...config,
        data: formData,
        headers: Object.assign(headers, {
          'Content-Type': ContentTypeEnum.FORM_DATA,
          ignoreCancelToken: true,
          token: getToken()
        })
      },
      { isNeedSign: false, ...requestOptions }
    )
  }
  /**
   * post请求
   * T:返回数据类型
   * D:请求参数类型
   * */
  post<T = any, D = any>(
    url: string,
    data: any,
    headerConfig?: AxiosRequestConfig,
    requestOptions?: RequestOptions
  ): Promise<T> {
    const postData: any = {}
    if (data && data.params) {
      postData.params = data.params
      if (data.data) {
        postData.data = data.data
      }
    } else {
      postData.data = data
    }

    return this.request<T, D>(
      {
        ...cloneDeep(headerConfig),
        url,
        method: 'POST',
        ...postData,
        requestOptions
      },
      requestOptions
    )
  }
  get<T = any, D = any>(
    url: string,
    data: any,
    headerConfig?: AxiosRequestConfig,
    requestOptions?: RequestOptions
  ): Promise<T> {
    return this.request<T, D>(
      {
        ...cloneDeep(headerConfig),
        url,
        method: 'GET',
        params: data,
        requestOptions
      },
      requestOptions
    )
  }
  delete<T = any, D = any>(
    url: string,
    data: any,
    headerConfig?: AxiosRequestConfig,
    requestOptions?: RequestOptions
  ): Promise<T> {
    const postData: any = {}
    if (data && data.params) {
      postData.params = data.params
      if (data.data) {
        postData.data = data.data
      }
    } else {
      postData.data = data
    }
    return this.request<T, D>(
      { ...cloneDeep(headerConfig), url, method: 'DELETE', ...postData },
      requestOptions
    )
  }
  cancelRequest(url: string | string[]) {
    const urlList = Array.isArray(url) ? url : [url]
    for (const _url of urlList) {
      abortControllerMap.get(_url)?.abort()
      abortControllerMap.delete(_url)
    }
  }
  cancelAllRequest() {
    // eslint-disable-next-line
    for (const [_, controller] of abortControllerMap) {
      controller.abort()
    }
    abortControllerMap.clear()
  }
  request<T = any, D = any>(
    config: AxiosRequestConfig<D>,
    requestOptions?: RequestOptions
  ): Promise<T> {
    let conf = config
    const defaultOptions = this.options.requestOptions
    const opt = Object.assign({}, defaultOptions, requestOptions)

    const transform = this.getTransform()
    const { beforeRequestHook, afterResponseHook } = transform || {}
    // 请求拦截处理
    if (beforeRequestHook && isFunc(beforeRequestHook)) {
      conf = beforeRequestHook(conf, opt)
    }
    // @ts-ignore
    conf.requestOptions = opt
    return new Promise((resolve, reject) => {
      this.instance
        .request<any, AxiosResponse<Result>>(conf)
        .then((res: AxiosResponse<Result>) => {
          // 响应拦截处理
          if (afterResponseHook && isFunc(afterResponseHook)) {
            try {
              const ret = afterResponseHook(res, opt)
              resolve(ret)
            } catch (err) {
              reject(err || new Error('request error'))
            }
            return
          }
          resolve(res as unknown as Promise<T>)
        })
        .catch(err => {
          // 错误处理
          reject(err)
        })
    })
  }
  getTransform() {
    const { transform } = this.options
    return transform
  }
}
